import * as Yup from 'yup'
import { TFunction } from '_metronic/i18n/Metronici18n'

const preenchido = (valor: unknown) => String(valor ?? '').trim() !== ''

/**
 * Validação do formulário de LME/APAC depois da assinatura. Só cobra o bloco que está na tela;
 * com "usar o CID e a anamnese do LME", a APAC não pede os dois de novo. Limites do backend:
 * anamnese até 600 caracteres e tratamento prévio até 100. Os meses do LME vão a partir do 1º e
 * sem pular: o backend descarta os vazios e renumera os demais.
 */
export const dadosSusSchema = (
  t: TFunction,
  {
    lme,
    apac,
    medicamentosLme,
    examesApac,
  }: { lme: boolean; apac: boolean; medicamentosLme: number[]; examesApac: number[] }
) => {
  const obrigatorio = t('prescricao.sus.obrigatorio')
  const mesLme = Yup.mixed().test('mes-lme', function (valor) {
    const indice = Number(/\[(\d+)\]$/.exec(this.path)?.[1] ?? 0)
    const meses = (this.parent ?? []) as unknown[]
    if (preenchido(valor)) {
      const numero = Number(valor)
      // O backend recebe inteiro: 0,5 viraria 0 sem erro.
      return (
        (Number.isInteger(numero) && numero > 0) ||
        this.createError({ message: t('prescricao.sus.inteiro_positivo') })
      )
    }
    if (indice === 0) return this.createError({ message: t('prescricao.sus.lme_mes1') })
    return (
      !meses.slice(indice + 1).some(preenchido) ||
      this.createError({ message: t('prescricao.sus.lme_sem_pular') })
    )
  })
  const anamnese = Yup.string()
    .trim()
    .required(obrigatorio)
    .max(600, t('prescricao.sus.anamnese_max'))

  return Yup.object().shape({
    ...(lme && {
      cidLme: Yup.object().nullable().required(obrigatorio),
      anamneseLme: anamnese,
      mesesLme: Yup.object().shape(
        Object.fromEntries(medicamentosLme.map((id) => [String(id), Yup.array().of(mesLme)]))
      ),
      temTratamento: Yup.boolean().nullable().required(obrigatorio),
      tratamento: Yup.string()
        .trim()
        .max(100, t('prescricao.sus.tratamento_max'))
        .when('temTratamento', { is: true, then: (schema) => schema.required(obrigatorio) }),
    }),
    ...(apac && {
      cidApac: Yup.object()
        .nullable()
        .when('reusar', { is: false, then: (schema) => schema.required(obrigatorio) }),
      anamneseApac: Yup.string().when('reusar', { is: false, then: () => anamnese }),
      quantidades: Yup.object().shape(
        Object.fromEntries(
          examesApac.map((id) => [
            String(id),
            Yup.number()
              .typeError(obrigatorio)
              .required(obrigatorio)
              .integer(t('prescricao.sus.inteiro_positivo'))
              .min(1, t('prescricao.sus.inteiro_positivo')),
          ])
        )
      ),
    }),
  })
}
